import { useEffect, useState, useCallback } from 'react'
import { X, Download, ChevronLeft, ChevronRight } from 'lucide-react'
import DOMPurify from 'dompurify'
import { emailApi } from '../api/email'
import toast from 'react-hot-toast'

interface Attachment {
  filename: string
  size: number
  mimeType: string
}

interface Props {
  attachments: Attachment[]
  initialIndex: number
  companyId: string
  messageId: string
  onClose: () => void
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const isPreviewable = (mimeType: string) =>
  mimeType.startsWith('image/') || mimeType === 'application/pdf' || mimeType.startsWith('video/')

export function EmailAttachmentViewerModal({ attachments, initialIndex, companyId, messageId, onClose }: Props) {
  const [idx, setIdx] = useState(initialIndex)
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)

  const attachment = attachments[idx]

  const loadBlob = useCallback(async (i: number) => {
    setLoading(true)
    setError(false)
    setBlobUrl(prev => { if (prev) URL.revokeObjectURL(prev); return null })
    try {
      const res = await emailApi.downloadAttachment(companyId, messageId, i)
      const mime = attachments[i].mimeType
      let blob: Blob
      if (mime === 'image/svg+xml') {
        // Sanitize SVG before re-wrapping. The backend serves SVG as octet-stream
        // to prevent direct-URL script execution, but <img> needs the correct MIME
        // type to render. DOMPurify strips scripts so even navigating to the blob
        // URL directly cannot execute attacker-controlled code.
        const clean = DOMPurify.sanitize(await res.data.text(), {
          USE_PROFILES: { svg: true, svgFilters: true },
        })
        blob = new Blob([clean], { type: 'image/svg+xml' })
      } else {
        blob = res.data
      }
      setBlobUrl(URL.createObjectURL(blob))
    } catch {
      setError(true)
    } finally {
      setLoading(false)
    }
  }, [companyId, messageId])

  useEffect(() => { loadBlob(idx) }, [idx, loadBlob])

  useEffect(() => () => { if (blobUrl) URL.revokeObjectURL(blobUrl) }, [blobUrl])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowLeft' && idx > 0) setIdx(i => i - 1)
      if (e.key === 'ArrowRight' && idx < attachments.length - 1) setIdx(i => i + 1)
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [idx, attachments.length, onClose])

  const handleDownload = async () => {
    try {
      const res = await emailApi.downloadAttachment(companyId, messageId, idx)
      const url = URL.createObjectURL(res.data)
      const a = document.createElement('a')
      a.href = url
      a.download = attachment.filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch {
      toast.error('Download failed')
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/75 z-50 flex items-center justify-center p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="bg-gray-900 rounded-xl flex flex-col w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700 flex-shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-white text-sm font-medium truncate">{attachment.filename}</span>
            <span className="text-gray-500 text-xs flex-shrink-0">{formatBytes(attachment.size)}</span>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0 ml-4">
            <button onClick={handleDownload} className="btn btn-ghost btn-sm p-1.5 text-gray-400 hover:text-white" title="Download">
              <Download className="w-4 h-4" />
            </button>
            <button onClick={() => setIdx(i => i - 1)} disabled={idx === 0}
              className="btn btn-ghost btn-sm p-1.5 text-gray-400 hover:text-white disabled:opacity-30" title="Previous (←)">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button onClick={() => setIdx(i => i + 1)} disabled={idx === attachments.length - 1}
              className="btn btn-ghost btn-sm p-1.5 text-gray-400 hover:text-white disabled:opacity-30" title="Next (→)">
              <ChevronRight className="w-4 h-4" />
            </button>
            <button onClick={onClose} className="btn btn-ghost btn-sm p-1.5 text-gray-400 hover:text-red-400" title="Close (Esc)">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Preview area */}
        <div className="flex-1 overflow-auto bg-gray-950 flex items-center justify-center min-h-[300px]">
          {loading && <div className="text-gray-500 text-sm">Loading…</div>}
          {error && (
            <div className="text-center space-y-3">
              <p className="text-gray-400 text-sm">Preview not available</p>
              <button onClick={handleDownload} className="btn btn-ghost btn-sm text-gray-400 hover:text-white">
                <Download className="w-4 h-4 mr-1" /> Download instead
              </button>
            </div>
          )}
          {!loading && !error && blobUrl && attachment.mimeType.startsWith('image/') && (
            <img src={blobUrl} alt={attachment.filename} className="max-w-full max-h-full object-contain" />
          )}
          {!loading && !error && blobUrl && attachment.mimeType === 'application/pdf' && (
            <iframe src={blobUrl} title={attachment.filename} className="w-full h-full min-h-[500px] border-0" />
          )}
          {!loading && !error && blobUrl && attachment.mimeType.startsWith('video/') && (
            <video src={blobUrl} controls autoPlay className="max-w-full max-h-full" />
          )}
          {!loading && !error && blobUrl && !isPreviewable(attachment.mimeType) && (
            <div className="text-center space-y-3">
              <p className="text-gray-400 text-sm">No preview available for this file type</p>
              <button onClick={handleDownload} className="btn btn-ghost btn-sm text-gray-400 hover:text-white">
                <Download className="w-4 h-4 mr-1" /> Download
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t border-gray-700 flex items-center justify-end flex-shrink-0">
          <span className="text-gray-600 text-xs">
            {idx + 1} of {attachments.length} · ← → to navigate · Esc to close
          </span>
        </div>
      </div>
    </div>
  )
}
